import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import { platformSetupService } from '../../../services/apiService';
import { ui } from './authFieldStyles';
import Enable2faPanel from './Enable2faPanel';

const PasswordField = ({ label, value, onChange, disabled, autoFocus }) => {
  const [show, setShow] = useState(false);
  return (
    <div style={ui.field}>
      <label style={ui.label}>{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete="new-password"
          placeholder="••••••••"
          style={{ ...ui.input, paddingRight: '46px' }}
        />
        <button type="button" onClick={() => setShow((s) => !s)} tabIndex={-1} style={ui.eyeBtn}>
          <Icon name={show ? 'EyeOff' : 'Eye'} size={18} color="#94A3B8" />
        </button>
      </div>
    </div>
  );
};

/**
 * First-login bootstrap for a super-admin: reset the temporary password (required), sign in with
 * it, then optionally enable 2FA. 2FA enrollment uses the session-guarded self-service endpoints
 * AFTER sign-in, so it's genuinely optional and skippable.
 */
const FirstLoginSetup = ({ email, setupToken, onComplete }) => {
  const { login } = useAuth();
  const [step, setStep] = useState('password'); // 'password' | 'offer2fa' | 'enable2fa'
  const [pw, setPw] = useState({ p1: '', p2: '' });
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (pw.p1.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    if (pw.p1 !== pw.p2) { setErr('Passwords do not match.'); return; }
    setErr('');
    setLoading(true);
    try {
      await platformSetupService.setPassword({ setup_token: setupToken, new_password: pw.p1 });
      // Password reset consumed the setup token; sign in with the new password (2FA not enabled
      // yet, so this yields a session directly).
      const res = await login(email, pw.p1);
      if (res.status !== 'session') {
        setErr(res.error || 'Password saved, but automatic sign-in failed. Please return to login.');
        setLoading(false);
        return;
      }
      setLoading(false);
      setStep('offer2fa');
    } catch (e2) {
      setLoading(false);
      setErr(e2.response?.data?.error?.message || 'Could not set the password. The setup link may have expired — please return to login.');
    }
  };

  if (step === 'password') {
    return (
      <div style={ui.card}>
        <h1 style={ui.heading}>Set your password</h1>
        <p style={ui.sub}>First-time sign-in for <strong>{email}</strong>. Choose a new password to continue.</p>
        <form onSubmit={submitPassword} noValidate>
          {err && <div style={ui.errBanner}><Icon name="AlertCircle" size={14} color="#DC2626" />{err}</div>}
          <PasswordField label="New password" value={pw.p1} onChange={(v) => { setPw((s) => ({ ...s, p1: v })); if (err) setErr(''); }} disabled={loading} autoFocus />
          <PasswordField label="Confirm password" value={pw.p2} onChange={(v) => { setPw((s) => ({ ...s, p2: v })); if (err) setErr(''); }} disabled={loading} />
          <button type="submit" disabled={loading} style={{ ...ui.cta, ...(loading ? ui.ctaDisabled : {}) }}>
            {loading ? 'Saving…' : 'Set password & continue'}
          </button>
        </form>
      </div>
    );
  }

  if (step === 'offer2fa') {
    return (
      <div style={ui.card}>
        <h1 style={ui.heading}>Enable two-factor authentication?</h1>
        <p style={ui.sub}>Optional. Add an extra layer of security now, or skip and enable it later from Security settings.</p>
        <button type="button" onClick={() => setStep('enable2fa')} style={ui.cta}>Enable 2FA now</button>
        <button type="button" onClick={onComplete} style={ui.secondaryBtn}>Skip for now</button>
      </div>
    );
  }

  // enable2fa
  return (
    <div style={ui.card}>
      <Enable2faPanel onDone={onComplete} onCancel={() => setStep('offer2fa')} />
    </div>
  );
};

export default FirstLoginSetup;
